import {
  Component,
  ElementRef,
  inject,
  Input,
  OnInit,
  QueryList,
  signal,
  ViewChild,
  ViewChildren,
} from "@angular/core";

import { GlobalService } from "@bitwarden/common/services/global/global.service";

import { GLOSS_CREDITS_ACCOUNT_NAME } from "@bitwarden/web-vault/app/models/constants/global.constants";
import { TransactionDirection } from "@bitwarden/web-vault/app/models/enum/transactionDirection";
import { TransactionStatusEnum } from "@bitwarden/web-vault/app/models/enum/transactionType";
import { TransactionForm } from "@bitwarden/web-vault/app/models/types/account.types";
import { Origin } from "@bitwarden/web-vault/app/models/types/general-types";
import { CurrencyType } from "@bitwarden/web-vault/app/models/types/location-currency.types";
import { transactionDataType } from "@bitwarden/web-vault/app/models/types/transactionData.types";
import { AccountView } from "@bitwarden/web-vault/app/models/view/account/account.view";
import {
  FilePreviewGrid,
  FileUploadService,
} from "@bitwarden/web-vault/app/services/DataService/file-upload/file-upload.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { DialogFactory } from "@bitwarden/web-vault/app/components/dialog/dialogFactory";
import {
  AmountMode,
  buildMappedRows,
  currencyDecimalPlaces,
  detectAmountColumn,
  detectBalanceColumn,
  detectCategoryColumn,
  detectCreditColumn,
  detectCurrencyColumn,
  detectDateColumn,
  detectDateFormat,
  detectDebitColumn,
  detectDescriptionColumn,
  MappedStatementRow,
  StatementMapping,
  STATEMENT_DATE_FORMATS,
  summarizeStatement,
} from "@bitwarden/web-vault/app/shared/utils/file-upload/statement-mapping.util";
import { closeAfterExitAnimation } from "@bitwarden/web-vault/app/shared/utils/exit-animation.util";

export type StatementImportResult = {
  account: AccountView;
  /** Built transactions using the selected account currency. */
  transactions: transactionDataType[];
  /** The parsed statement rows, for the cabinet preview pane (mirrors the review table). */
  mappedRows: MappedStatementRow[];
  /** Earliest transaction date (yyyy-MM-dd) — prefills the link-account start date. */
  fromDate: string;
  /** Latest transaction date (yyyy-MM-dd) — prefills the link-account end date. */
  toDate: string;
  /** Opening balance from the statement, or undefined when no balance column. */
  openingBalance?: string;
  /** Closing balance from the statement, or undefined when no balance column. */
  closingBalance?: string;
};

/**
 * Column-mapping dialog shown after a tabular bank statement (csv/xls/xlsx) is
 * picked in the cabinet. The user maps statement columns onto a transaction
 * (account, currency, amount, date, description) and previews the result before
 * the rows are turned into transactions. Balance columns are never imported.
 */
@Component({
  selector: "app-statement-import-mapping",
  templateUrl: "./statement-import-mapping.component.html",
})
export class StatementImportMappingComponent implements OnInit {
  /** The statement the user picked. */
  @Input() file: File;
  /** Closes the host dialog. */
  @Input() closeDialog: () => void = () => undefined;
  /** Called with the resolved account + built transactions when the user continues. */
  @Input() onConfirm: (result: StatementImportResult) => void = () => undefined;
  /** Reports accounts created from the embedded picker so the host can roll them back on cancel. */
  @Input() onAccountCreated: (account: AccountView) => void = () => undefined;

  private fileUploadService = inject(FileUploadService);
  private userStore = inject(UserStoreService);
  private globalService = inject(GlobalService);
  private dialogFactory = new DialogFactory();

  @ViewChildren("previewRowElement") private previewRows: QueryList<ElementRef<HTMLElement>>;
  @ViewChild("invalidRowsScreen") private invalidRowsScreen?: ElementRef<HTMLElement>;

  protected readonly TransactionDirection = TransactionDirection;
  protected readonly dateFormats: string[] = STATEMENT_DATE_FORMATS;
  protected readonly previewRowLimit = 50;

  protected isLoading = signal(true);
  protected isSaving = signal(false);

  protected headers: string[] = [];
  protected accounts: AccountView[] = [];
  protected mappedRows: MappedStatementRow[] = [];
  protected invalidRows: MappedStatementRow[] = [];
  protected invalidRowIndexes = new Set<number>();
  protected previewStartIndex = 0;
  protected showInvalidRowsScreen = false;

  protected grid: FilePreviewGrid = { headers: [], rows: [] };

  /** Form state (bound via ngModel). */
  protected selectedAccountId = "";
  /** "single" = one signed amount column; "split" = debit/credit columns. */
  protected amountMode: AmountMode = "single";
  protected amountIndex = -1;
  protected debitIndex = -1;
  protected creditIndex = -1;
  protected dateIndex = -1;
  /** True when a date column was auto-detected (hides the column picker). */
  protected dateAutoDetected = false;
  protected dateFormat = "";
  protected descriptionIndex = -1;
  protected categoryIndex = -1;
  /** -1 when the statement has no running-balance column. */
  protected balanceIndex = -1;
  /** -1 when the statement has no currency column (used only for the FX-mismatch guard). */
  protected currencyIndex = -1;

  async ngOnInit(): Promise<void> {
    const preference = this.userStore.preferences.preferenceView();
    this.loadAccounts();
    if (this.accounts.length > 0) {
      this.selectedAccountId = this.accounts[0].id;
    }

    try {
      const sheets = await this.fileUploadService.getSheetGrids(this.file);
      // Importers consume the first sheet only (multi-sheet is preview-only).
      this.grid = sheets[0]?.grid ?? { headers: [], rows: [] };
      this.headers = this.grid.headers ?? [];
      this.autoDetectColumns(preference?.dateFormat);
    } catch {
      this.globalService.showWarningMessage("warning", "filePreviewNotAvailable");
    } finally {
      this.isLoading.set(false);
    }
  }

  private autoDetectColumns(preferenceDateFormat?: string): void {
    this.amountIndex = detectAmountColumn(this.headers);
    this.debitIndex = detectDebitColumn(this.headers);
    this.creditIndex = detectCreditColumn(this.headers);
    // Prefer split mode whenever BOTH a debit and a credit column exist — even
    // if one of them ("Debit Amount"/"Credit Amount") also matched the single
    // amount regex. Reading such a statement in single mode drops every credit
    // row and mis-signs debits as money-in. A lone amount column stays single.
    if (this.debitIndex >= 0 && this.creditIndex >= 0) {
      this.amountMode = "split";
    }
    this.descriptionIndex = detectDescriptionColumn(this.headers);
    this.categoryIndex = detectCategoryColumn(this.headers);
    this.balanceIndex = detectBalanceColumn(this.headers);
    this.currencyIndex = detectCurrencyColumn(this.headers);

    const detectedDateIndex = detectDateColumn(this.headers);
    this.dateAutoDetected = detectedDateIndex >= 0;
    this.dateIndex = detectedDateIndex >= 0 ? detectedDateIndex : this.headers.length ? 0 : -1;
    this.detectDateFormatForColumn(preferenceDateFormat);
  }

  /** Re-detect the date format from the currently selected date column's sample values. */
  private detectDateFormatForColumn(preferenceDateFormat?: string): void {
    const samples = (this.grid.rows ?? [])
      .slice(0, 20)
      .map((row) => row[this.dateIndex])
      .filter((v) => v != null && String(v).trim() !== "");
    this.dateFormat =
      detectDateFormat(samples) ?? preferenceDateFormat ?? STATEMENT_DATE_FORMATS[0];
  }

  /** User re-picked the date column: re-detect the format to match the new column. */
  protected onDateColumnChange(): void {
    this.clearInvalidRows();
    this.detectDateFormatForColumn(this.userStore.preferences.preferenceView()?.dateFormat);
  }

  /** Slides the screen out first (see .mobile-slide-in-page), then removes it. */
  protected closeInvalidRowsScreen(): void {
    closeAfterExitAnimation(this.invalidRowsScreen?.nativeElement, () => {
      this.showInvalidRowsScreen = false;
    });
  }

  protected get accountName(): string {
    return this.accounts.find((a) => a.id === this.selectedAccountId)?.displayName ?? "";
  }

  protected get selectedAccountCurrency(): string {
    return this.resolveAccount(false)?.currency ?? "";
  }

  protected get previewGridRows(): Array<{ index: number; cells: string[] }> {
    const endIndex = this.invalidRows.length
      ? this.grid.rows.length
      : this.previewStartIndex + this.previewRowLimit;
    return (this.grid.rows ?? []).slice(this.previewStartIndex, endIndex).map((cells, offset) => ({
      index: this.previewStartIndex + offset,
      cells,
    }));
  }

  protected get previewShowingEnd(): number {
    if (this.invalidRows.length) {
      return this.grid.rows.length;
    }
    return Math.min(this.previewStartIndex + this.previewRowLimit, this.grid.rows.length);
  }

  protected get isPreviewShifted(): boolean {
    return this.previewStartIndex > 0;
  }

  protected isInvalidPreviewRow(rowIndex: number): boolean {
    return this.invalidRowIndexes.has(rowIndex);
  }

  /** Statement imports can only link manual accounts; hide Gloss Credits too. */
  private loadAccounts(): void {
    this.accounts = (this.userStore.accounts.accountViews() ?? []).filter(
      (account) => account.origin === Origin.manual && account.name !== GLOSS_CREDITS_ACCOUNT_NAME,
    );
  }

  /**
   * Launch the manual account-creation flow (no balance step). The created
   * account is persisted immediately, then selected in the dropdown.
   */
  protected createAccount(): void {
    this.dialogFactory.createAccountForSelection((account) => {
      this.loadAccounts();
      this.selectedAccountId = account.id;
      this.onAccountCreated(account);
    });
  }

  private buildMapping(): StatementMapping {
    return {
      amountMode: this.amountMode,
      amountIndex: this.amountIndex,
      debitIndex: this.debitIndex,
      creditIndex: this.creditIndex,
      dateIndex: this.dateIndex,
      dateFormat: this.dateFormat,
      descriptionIndex: this.descriptionIndex,
      categoryIndex: this.categoryIndex,
      balanceIndex: this.balanceIndex,
    };
  }

  private validateMapping(): boolean {
    if (!this.accountName) {
      this.globalService.showWarningMessage("warning", "pleaseSelectAnAccount");
      return false;
    }
    if (this.amountMode === "split") {
      if (this.debitIndex < 0 || this.creditIndex < 0) {
        this.globalService.showWarningMessage("warning", "pleaseSelectDebitCreditColumns");
        return false;
      }
    } else if (this.amountIndex < 0) {
      this.globalService.showWarningMessage("warning", "pleaseSelectAmountColumn");
      return false;
    }
    if (this.dateIndex < 0 || !this.dateFormat) {
      this.globalService.showWarningMessage("warning", "pleaseSelectDateColumn");
      return false;
    }
    if (!this.validateStatementCurrency()) {
      return false;
    }
    return true;
  }

  /**
   * Statement import does NOT convert FX — amounts are stamped with the linked
   * account's currency. When the statement carries a currency column, block the
   * import if any row's currency differs from the account's, so raw foreign
   * amounts can't be silently relabelled (e.g. USD numbers saved as AUD).
   * No currency column -> nothing to compare, so the guard passes.
   */
  private validateStatementCurrency(): boolean {
    if (this.currencyIndex < 0) {
      return true;
    }
    const accountCurrency = this.selectedAccountCurrency.trim().toUpperCase();
    if (!accountCurrency) {
      return true;
    }
    const mismatch = (this.grid.rows ?? []).some((row) => {
      // Only compare cells that actually look like an ISO-4217 code. This still
      // fires on a genuine USD-vs-AUD mismatch while ignoring symbol cells ("$"),
      // numeric cells (from a mis-detected "Foreign Currency Amount" header), and
      // blanks — which otherwise blocked same-currency imports with no UI override
      // to recover (#3932).
      const code = (row[this.currencyIndex] ?? "").trim().toUpperCase();
      return /^[A-Z]{3}$/.test(code) && code !== accountCurrency;
    });
    if (mismatch) {
      this.globalService.showWarningMessage("warning", "statementCurrencyMismatch");
      return false;
    }
    return true;
  }

  /**
   * Map -> Confirm: parse the rows, resolve the account, then hand
   * everything back to the cabinet flow and close the dialog (no separate review step).
   *
   * Note: no transactions or categories are persisted here. Category names are
   * carried on the pending transaction form and resolved only after the file save
   * succeeds.
   */
  protected async map(): Promise<void> {
    this.clearInvalidRows();
    if (!this.validateMapping()) {
      return;
    }
    // Parse amounts with the linked account's currency precision so 3-decimal
    // currencies (KWD, BHD, …) aren't misread as thousands-grouped and inflated ~1000x.
    const mappedRows = buildMappedRows(
      this.grid,
      this.buildMapping(),
      currencyDecimalPlaces(this.selectedAccountCurrency),
    );
    if (mappedRows.length === 0) {
      this.globalService.showWarningMessage("warning", "noTransactionsFoundInStatement");
      return;
    }
    if (mappedRows.some((row) => !row.valid)) {
      this.showInvalidRows(mappedRows);
      this.globalService.showWarningMessage("warning", "invalidStatementRowsFound");
      return;
    }
    this.mappedRows = mappedRows;

    this.isSaving.set(true);
    try {
      const account = this.resolveAccount();
      if (!account) {
        return;
      }
      const accountCurrency = account.currency as CurrencyType;
      const summary = summarizeStatement(this.mappedRows);
      this.mappedRows = this.mappedRows.map((row) => ({ ...row, currency: accountCurrency }));

      const transactions: transactionDataType[] = this.mappedRows.map((row) => ({
        account,
        formData: this.toTransactionForm(row, account.currency),
      }));

      this.onConfirm({
        account,
        transactions,
        mappedRows: this.mappedRows,
        fromDate: summary?.fromDate ?? "",
        toDate: summary?.toDate ?? "",
        openingBalance: summary?.openingBalance,
        closingBalance: summary?.closingBalance,
      });
      this.closeDialog();
    } catch {
      this.globalService.showWarningMessage("error", "transactionFailed");
    } finally {
      this.isSaving.set(false);
    }
  }

  private toTransactionForm(
    row: MappedStatementRow,
    accountCurrency: CurrencyType,
  ): TransactionForm {
    return {
      amount: row.amount,
      quantity: row.amount,
      currency: accountCurrency,
      categoryName: row.category || undefined,
      date: row.date,
      transactionStatus: TransactionStatusEnum.transaction,
      description: row.description,
      type: row.type,
    };
  }

  private resolveAccount(showWarning = true): AccountView | null {
    const account = this.accounts.find((a) => a.id === this.selectedAccountId);
    if (!account) {
      if (showWarning) {
        this.globalService.showWarningMessage("warning", "pleaseSelectAnAccount");
      }
      return null;
    }
    return account;
  }

  private showInvalidRows(mappedRows: MappedStatementRow[]): void {
    this.mappedRows = mappedRows;
    this.invalidRows = mappedRows.filter((row) => !row.valid);
    this.invalidRowIndexes = new Set(this.invalidRows.map((row) => row.sourceRowIndex));
    const firstInvalidRowIndex = this.invalidRows[0]?.sourceRowIndex ?? 0;
    this.previewStartIndex = Math.max(0, firstInvalidRowIndex - 3);
    this.showInvalidRowsScreen = this.isMobileScreen();
    if (!this.showInvalidRowsScreen) {
      this.scrollToFirstInvalidRow(firstInvalidRowIndex);
    }
  }

  protected clearInvalidRows(): void {
    this.invalidRows = [];
    this.invalidRowIndexes = new Set<number>();
    this.previewStartIndex = 0;
    this.showInvalidRowsScreen = false;
  }

  private scrollToFirstInvalidRow(rowIndex: number): void {
    setTimeout(() => {
      const row = this.previewRows?.find(
        (element) => element.nativeElement.dataset.rowIndex === String(rowIndex),
      );
      row?.nativeElement.scrollIntoView({ block: "center", inline: "nearest" });
    });
  }

  private isMobileScreen(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia("(max-width: 639px)").matches
    );
  }
}
